import FlickityAsNavFor from 'flickity-as-nav-for';
import debounce from '../util/debounce';
import throttle from '../util/throttle';
import getSiblings from '../util/siblings';

const selectors = {
  productContainer: '[data-product-container]',
  productSlideshow: '[data-product-slideshow]',
  productImage: '[data-product-single-media-wrapper]',
  productThumbs: '[data-product-single-media-thumbs]',
  productThumb: '[data-thumbnail]',
  productThumbLink: '[data-thumbnail-id]',
  deferredMediaButton: '[data-deferred-media-button]',
  mediaType: 'data-type',
  id: 'data-id',
  tabIndex: 'tabindex',
  arrows: 'data-arrows',
  dots: 'data-dots',
};

const classes = {
  active: 'active',
  sliderEnabled: 'flickity-enabled',
  scrollable: 'product-layout--scrollable',
  mediaHidden: 'media--hidden',
  focusEnabled: 'is-focused',
  thumbsArrows: 'productImgSlider-nav--arrows',
  isMoving: 'is-moving',
};

class InitSlider {
  constructor(section) {
    this.container = section.container;
    this.productContainer = this.container.querySelector(selectors.productContainer);
    this.scrollable = this.productContainer.classList.contains(classes.scrollable);
    this.slideshow = this.container.querySelector(selectors.productSlideshow);
    this.productImages = this.container.querySelectorAll(selectors.productImage);
    this.thumbs = this.container.querySelector(selectors.productThumbs);
    this.flkty = null;
    this.flktyMobile = null;
    this.resizeEvent = debounce(() => this.checkThumbsWidth(), 250);
    this.scrollEvent = throttle(() => this.stickyThumbs(), 50);
    this.resizeEventScroller = throttle(() => this.mobileScrollerSlider(), 250);

    if (this.productImages.length > 1) {
      this.init();
    }
  }

  init() {
    if (this.scrollable) {
      this.destroySlider();
      this.destroySliderNav();

      this.createScroller();
    } else {
      this.destroyScroller();

      this.createSlider();
      this.createSliderNav();
    }
  }

  createScroller() {
    let isPageLoaded = false;

    this.stickyThumbs();
    window.addEventListener('scroll', this.scrollEvent);
    window.addEventListener('load', () => {
      isPageLoaded = true;
    });

    const thumbsLinks = this.thumbs.querySelectorAll(selectors.productThumbLink);

    thumbsLinks.forEach((thumbsLink) => {
      thumbsLink.addEventListener('click', (e) => {
        const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

        e.preventDefault();

        if (windowWidth > theme.sizes.small) {
          const id = thumbsLink.parentNode.getAttribute(selectors.id);
          const selectedImage = this.slideshow.querySelector(`[${selectors.id}="${id}"]`);

          if (selectedImage != null && isPageLoaded) {
            const bottomMargin = parseInt(window.getComputedStyle(selectedImage).marginBottom.replace('px', ''));
            const selectedImageTop = selectedImage.getBoundingClientRect().top - document.body.getBoundingClientRect().top;
            const scrollPosition = selectedImageTop - theme.dimensions.headerScrolled - bottomMargin;

            window.scrollTo({
              top: scrollPosition,
              left: 0,
              behavior: 'smooth',
            });

            this.switchMedia(selectedImage);
          }
        }
      });
    });

    this.mobileScrollerSlider();
    window.addEventListener('resize', this.resizeEventScroller);
  }

  mobileScrollerSlider() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    if (windowWidth <= theme.sizes.small) {
      this.createSlider();
      this.createSliderNav();
    } else {
      this.destroySlider();
      this.destroySliderNav();
    }
  }

  destroyScroller() {
    window.removeEventListener('scroll', this.scrollEvent);
    window.removeEventListener('resize', this.resizeEventScroller);
  }

  destroySlider() {
    const isSliderInitialized = this.slideshow.classList.contains(classes.sliderEnabled);

    if (isSliderInitialized) {
      this.flkty.destroy();
    }
  }

  destroySliderNav() {
    const isSliderInitialized = this.thumbs.classList.contains(classes.sliderEnabled);

    if (isSliderInitialized) {
      this.flktyMobile.destroy();
    }

    window.removeEventListener('resize', this.resizeEvent);
  }

  createSlider() {
    const instance = this;
    const firstSlide = this.slideshow.querySelectorAll(`[${selectors.mediaType}]`)[0];
    const arrows = this.slideshow.getAttribute(selectors.arrows) === 'true';
    const dots = this.slideshow.getAttribute(selectors.dots) === 'true';

    const flickityOptions = {
      autoPlay: false,
      arrowShape: theme.icons.arrowNavSlider,
      prevNextButtons: arrows,
      contain: true,
      pageDots: dots,
      adaptiveHeight: true,
      wrapAround: true,
    };

    this.flkty = new FlickityAsNavFor(this.slideshow, flickityOptions);
    this.flkty.resize();

    if (firstSlide) {
      const firstType = firstSlide.getAttribute(selectors.mediaType);

      if (firstType === 'model' || firstType === 'video' || firstType === 'external_video') {
        this.flkty.options.draggable = false;
        this.flkty.updateDraggable();
      }
    }

    this.flkty.on('dragStart', function () {
      instance.slideshow.classList.add(classes.isMoving);
    });

    this.flkty.on('change', function (index) {
      let lastSLideIdx = index;

      const currentMedia = this.cells[lastSLideIdx].element;
      const newMedia = this.selectedElement;

      currentMedia.dispatchEvent(new CustomEvent('mediaHidden'));
      newMedia.classList.remove(classes.mediaHidden);
    });

    this.flkty.on('settle', function (index) {
      const currentMedia = this.selectedElement;
      const mediaType = currentMedia.getAttribute(selectors.mediaType);

      if (mediaType === 'model' || mediaType === 'video' || mediaType === 'external_video') {
        // fisrt boolean sets value, second option false to prevent refresh
        instance.flkty.options.draggable = false;
        instance.flkty.updateDraggable();
      } else {
        instance.flkty.options.draggable = true;
        instance.flkty.updateDraggable();
      }

      instance.switchMedia(currentMedia);
      instance.slideshow.classList.remove(classes.isMoving);
    });
  }

  createSliderNav() {
    if (!this.thumbs) {
      return;
    }

    const flickityOptionsMobile = {
      asNavFor: this.slideshow,
      pageDots: false,
      prevNextButtons: true,
      arrowShape: theme.icons.arrowNavSlider,
      groupCells: true,
      contain: true,
      on: {
        ready: () => {
          this.thumbs.setAttribute(selectors.tabIndex, '-1');
        },
      },
    };

    this.flktyMobile = new FlickityAsNavFor(this.thumbs, flickityOptionsMobile);
    this.flktyMobile.resize();

    this.checkThumbsWidth();
    window.addEventListener('resize', this.resizeEvent);

    const thumbsLinks = this.thumbs.querySelectorAll(selectors.productThumbLink);

    thumbsLinks.forEach((thumbsLink) => {
      thumbsLink.addEventListener('click', (e) => {
        e.preventDefault();
      });
    });
  }

  checkThumbsWidth() {
    const thumbs = this.thumbs.querySelectorAll(selectors.productThumb);
    const thumbsContainerPadding = parseInt(window.getComputedStyle(this.thumbs).paddingLeft.replace('px', '')) * 2;
    const thumbsContainerWidth = this.thumbs.offsetWidth - thumbsContainerPadding;
    let thumbsWidth = 0;

    thumbs.forEach((thumb) => {
      thumbsWidth += thumb.offsetWidth;
    });

    if (thumbsContainerWidth < thumbsWidth) {
      this.thumbs.classList.add(classes.thumbsArrows);
    } else {
      this.thumbs.classList.remove(classes.thumbsArrows);
    }
  }

  switchMedia(currentMedia) {
    const otherMedia = Array.prototype.filter.call(currentMedia.parentNode.children, function (child) {
      return child !== currentMedia;
    });
    const isFocusEnabled = document.body.classList.contains(classes.focusEnabled);

    if (isFocusEnabled) {
      currentMedia.dispatchEvent(new Event('focus'));
    }

    if (otherMedia.length) {
      otherMedia.forEach((element) => {
        element.classList.add(classes.mediaHidden);
        element.dispatchEvent(new CustomEvent('mediaHidden'));
      });
    }

    currentMedia.classList.remove(classes.mediaHidden);
    currentMedia.dispatchEvent(new CustomEvent('mediaVisible'));

    // Force media loading if slide becomes visible
    const deferredMedia = currentMedia.querySelector('deferred-media');
    if (deferredMedia && deferredMedia.getAttribute('loaded') !== true) {
      currentMedia.querySelector(selectors.deferredMediaButton).dispatchEvent(new Event('click', {bubbles: false}));
    }
  }

  stickyThumbs() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    if (windowWidth > theme.sizes.small) {
      [].slice
        .call(this.productImages, 0)
        .reverse()
        .forEach((productImage) => {
          const isVisible = visibilityHelper.isElementPartiallyVisible(productImage) || visibilityHelper.isElementTotallyVisible(productImage);
          const productImageId = productImage.getAttribute(selectors.id);

          if (isVisible) {
            this.switchMedia(productImage);
            const currentThumb = this.thumbs.querySelector(`[${selectors.id}="${productImageId}"]`);
            const siblings = getSiblings(currentThumb);

            currentThumb.classList.add(classes.active);
            siblings.forEach((sibling) => {
              sibling.classList.remove(classes.active);
            });
          }
        });
    }
  }
}

export default InitSlider;
